<template>
  <div class="pdf-controls">
    <div class="controls-group navigation">
      <v-btn icon @click="previousPage" :disabled="currentPage <= 1" dark>
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <span class="page-info"> {{ currentPage }} de {{ totalPages }} </span>

      <v-btn icon @click="nextPage" :disabled="currentPage >= totalPages" dark>
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <div class="controls-group search" v-if="searchAvailable">
      <v-text-field
        v-model="searchText"
        placeholder="Código..."
        @keyup.enter="performSearch"
        @input="onSearchInput"
        prepend-inner-icon="mdi-magnify"
        clearable
        dense
        dark
        outlined
        hide-details
        class="search-input"
      ></v-text-field>

      <v-btn
        icon
        @click="performSearch"
        :disabled="!searchText"
        small
        dark
        class="search-btn"
      >
        <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </div>

    <div class="controls-group search" v-else>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            placeholder="Búsqueda no disponible"
            prepend-inner-icon="mdi-magnify-off"
            dense
            dark
            outlined
            hide-details
            disabled
            class="search-input"
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <span>Este catálogo no tiene metadatos de búsqueda disponibles</span>
      </v-tooltip>
    </div>

    <div class="controls-group zoom">
      <v-btn icon @click="zoomOut" :disabled="scale <= minScale" small dark>
        <v-icon>mdi-magnify-minus</v-icon>
      </v-btn>

      <v-btn icon @click="resetZoom" small dark>
        <v-icon>mdi-magnify-scan</v-icon>
      </v-btn>

      <v-btn icon @click="zoomIn" :disabled="scale >= maxScale" small dark>
        <v-icon>mdi-magnify-plus</v-icon>
      </v-btn>

      <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "PdfControls",
  data() {
    return {
      searchText: "",
    };
  },
  props: {
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    scale: {
      type: Number,
      default: 1.5,
    },
    minScale: {
      type: Number,
      default: 0.5,
    },
    maxScale: {
      type: Number,
      default: 3,
    },
    searchAvailable: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    // Add keyboard navigation
    document.addEventListener("keydown", this.handleKeyPress);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeyPress);
  },
  methods: {
    previousPage() {
      if (this.currentPage > 1) {
        this.$emit("page-change", this.currentPage - 1);
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.$emit("page-change", this.currentPage + 1);
      }
    },

    zoomIn() {
      const newScale = Math.min(this.scale + 0.15, this.maxScale);
      this.$emit("scale-change", newScale);
    },

    zoomOut() {
      const newScale = Math.max(this.scale - 0.15, this.minScale);
      this.$emit("scale-change", newScale);
    },

    resetZoom() {
      // Calculate optimal zoom to fit PDF height in viewport
      this.$emit("fit-to-viewport");
    },

    performSearch() {
      if (this.searchText && this.searchText.trim()) {
        this.$emit("search", this.searchText.trim());
      }
    },

    onSearchInput() {
      // Emit search as user types (optional - for live search)
      if (this.searchText && this.searchText.trim().length > 2) {
        this.$emit("search-input", this.searchText.trim());
      } else if (!this.searchText) {
        // Clear search when input is empty
        this.$emit("search-clear");
      }
    },

    handleKeyPress(event) {
      // Only handle if no input is focused (including search input)
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      )
        return;

      switch (event.key) {
        case "ArrowLeft":
          this.previousPage();
          break;
        case "ArrowRight":
          this.nextPage();
          break;
        case "+":
        case "=":
          this.zoomIn();
          break;
        case "-":
        case "_":
          this.zoomOut();
          break;
        case "0":
          this.resetZoom(); // Now fits to viewport
          break;
      }
    },
  },
};
</script>

<style scoped>
.pdf-controls {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 30px;
  padding: 15px;
  background: #2c3e50;
  color: white;
}

.controls-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-info {
  font-size: 14px;
  padding: 0 15px;
  min-width: 150px;
  text-align: center;
}

.zoom-level {
  font-size: 14px;
  min-width: 50px;
  text-align: center;
}

.search-input {
  max-width: 250px;
  min-width: 200px;
}

.search-btn {
  margin-left: 5px;
}

.controls-group.search {
  display: flex;
  align-items: center;
}

/* Responsive design */
@media (max-width: 768px) {
  .pdf-controls {
    flex-direction: column;
    gap: 10px;
  }

  .controls-group {
    width: 100%;
    justify-content: center;
  }
}
</style>